import { useEffect, lazy, Suspense } from 'react'
import { Routes, Route, Navigate, Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  defaultLanguage,
  type SupportedLanguage,
} from '@/i18n'
import { localizedRoutes, type RouteKey } from '@/i18n/routes'
import { SkipToContent } from '@/components/layout'
import { PageSkeleton } from '@/components/PageSkeleton'

/* -----------------------------------------------------------------------------
 * Lazy page imports — keeps the initial bundle small. Each placeholder will be
 * replaced by its real implementation in later refactor sections.
 * -------------------------------------------------------------------------- */
const HomePage = lazy(() => import('@/pages/HomePage'))
const CompanyPage = lazy(() => import('@/pages/CompanyPage'))
const HowWeWorkPage = lazy(() => import('@/pages/HowWeWorkPage'))
const ProductsPage = lazy(() => import('@/pages/ProductsPage'))
const ProductsColorTechnicalPage = lazy(
  () => import('@/pages/ProductsColorTechnicalPage')
)
const ProductsScalpHairCarePage = lazy(
  () => import('@/pages/ProductsScalpHairCarePage')
)
const ProductsStylingPage = lazy(() => import('@/pages/ProductsStylingPage'))
const InsightsPage = lazy(() => import('@/pages/InsightsPage'))
const InsightsPrivateLabelHairCarePage = lazy(
  () => import('@/pages/InsightsPrivateLabelHairCarePage')
)
const InsightsPrivateLabelCosmeticCompanyPage = lazy(
  () => import('@/pages/InsightsPrivateLabelCosmeticCompanyPage')
)
const InsightsPrivateLabelCosmeticsPage = lazy(
  () => import('@/pages/InsightsPrivateLabelCosmeticsPage')
)
const InsightsPrivateLabelShampooPage = lazy(
  () => import('@/pages/InsightsPrivateLabelShampooPage')
)
const InsightsLuxuryPrivateLabelPage = lazy(
  () => import('@/pages/InsightsLuxuryPrivateLabelPage')
)
const InsightsHairCareManufacturersPage = lazy(
  () => import('@/pages/InsightsHairCareManufacturersPage')
)
const InsightsCosmeticsManufacturersPage = lazy(
  () => import('@/pages/InsightsCosmeticsManufacturersPage')
)
const MagazinePage = lazy(() => import('@/pages/MagazinePage'))
const MocraPage = lazy(() => import('@/pages/MocraPage'))
const QualityPolicyPage = lazy(() => import('@/pages/QualityPolicyPage'))
const SustainabilityPage = lazy(() => import('@/pages/SustainabilityPage'))
const WorkWithUsPage = lazy(() => import('@/pages/WorkWithUsPage'))
const BecameSupplierPage = lazy(() => import('@/pages/BecameSupplierPage'))
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage'))
const CookiePage = lazy(() => import('@/pages/CookiePage'))
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'))

/* -----------------------------------------------------------------------------
 * Route key → component map. The slug list lives in `localizedRoutes` and is
 * the single source of truth for paths.
 * -------------------------------------------------------------------------- */
const pageComponents: Record<
  Exclude<RouteKey, 'home'>,
  React.LazyExoticComponent<React.ComponentType>
> = {
  company: CompanyPage,
  howWeWork: HowWeWorkPage,
  products: ProductsPage,
  productsColorTechnical: ProductsColorTechnicalPage,
  productsScalpHairCare: ProductsScalpHairCarePage,
  productsStyling: ProductsStylingPage,
  insights: InsightsPage,
  insightsPrivateLabelHairCare: InsightsPrivateLabelHairCarePage,
  insightsPrivateLabelCosmeticCompany: InsightsPrivateLabelCosmeticCompanyPage,
  insightsPrivateLabelCosmetics: InsightsPrivateLabelCosmeticsPage,
  insightsPrivateLabelShampoo: InsightsPrivateLabelShampooPage,
  insightsLuxuryPrivateLabel: InsightsLuxuryPrivateLabelPage,
  insightsHairCareManufacturers: InsightsHairCareManufacturersPage,
  insightsCosmeticsManufacturers: InsightsCosmeticsManufacturersPage,
  magazine: MagazinePage,
  mocra: MocraPage,
  qualityPolicy: QualityPolicyPage,
  sustainability: SustainabilityPage,
  workWithUs: WorkWithUsPage,
  becameSupplier: BecameSupplierPage,
  privacy: PrivacyPage,
  cookie: CookiePage,
}

function LanguageWrapper({ lang }: { lang: SupportedLanguage }) {
  const { i18n } = useTranslation()

  useEffect(() => {
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang)
    }
  }, [lang, i18n])

  return (
    <>
      <SkipToContent />
      <Suspense fallback={<PageSkeleton />}>
        <Outlet />
      </Suspense>
    </>
  )
}

/**
 * Builds the per-language route tree. Slugs come from `localizedRoutes`
 * and may include nested segments (e.g. `prodotti/colore-e-tecnici`).
 */
function renderLocalizedRoutes(lang: SupportedLanguage) {
  return (Object.keys(pageComponents) as Array<keyof typeof pageComponents>).map(
    (key) => {
      const slug = localizedRoutes[key][lang]
      const Component = pageComponents[key]
      return <Route key={key} path={slug} element={<Component />} />
    }
  )
}

export function LanguageRouter() {
  return (
    <Routes>
      {/* Redirect root to default language */}
      <Route
        path="/"
        element={<Navigate to={`/${defaultLanguage}`} replace />}
      />

      {/* Italian tree */}
      <Route path="/it" element={<LanguageWrapper lang="it" />}>
        <Route index element={<HomePage />} />
        {renderLocalizedRoutes('it')}
        <Route path="*" element={<NotFoundPage />} />
      </Route>

      {/* English tree */}
      <Route path="/en" element={<LanguageWrapper lang="en" />}>
        <Route index element={<HomePage />} />
        {renderLocalizedRoutes('en')}
        <Route path="*" element={<NotFoundPage />} />
      </Route>

      {/* Generic catch-all → default-language 404 */}
      <Route
        path="*"
        element={<Navigate to={`/${defaultLanguage}/404`} replace />}
      />
    </Routes>
  )
}
